import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../reducers';
import { FetchStatus } from '@server/fetch_status.type';
import type { CrmOrg } from '../../../types/CrmOrgs';
import { OrgType } from '../../../apis/facades/types/crmOrgsApiFacade.types';

type CrmOrgObjectTypesInfo = {
  loadingObjectTypes: boolean;
  objectTypes?: ObjectTypeName[];
};

interface CrmOrgsObjectTypesMap {
  [crmOrgId: string]: CrmOrgObjectTypesInfo;
}
export interface EnvironmentsState {
  crmOrgsWasLoaded: boolean;
  crmOrgsAreReady: boolean; // This is set to true once the updateDefaultCreationEnv Heuristic is done
  crmOrgs: CrmOrg[];
  crmOrgsMap: CrmOrgsObjectTypesMap;
}

const initialState: EnvironmentsState = {
  crmOrgsWasLoaded: false,
  crmOrgsAreReady: false,
  crmOrgs: [], //TODO can move this inside crmOrgsMap too
  crmOrgsMap: {},
};

const getLabel = (obj?: ObjectTypeName) => obj?.label || obj?.objectType || '';
const sortByLabel = (objA: ObjectTypeName, objB: ObjectTypeName) =>
  getLabel(objA).localeCompare(getLabel(objB));

export const environmentsSlice = createSlice({
  name: 'environments',
  initialState,
  reducers: {
    setCrmOrgs: (state, action: PayloadAction<{ crmOrgs: CrmOrg[]; hubspotAsOrg: boolean }>) => {
      if (!action.payload.hubspotAsOrg) {
        state.crmOrgs = action.payload.crmOrgs.filter(
          (crmOrg: CrmOrg) => crmOrg.orgType !== OrgType.Hubspot,
        );
      } else {
        state.crmOrgs = action.payload.crmOrgs.map((crmOrg: CrmOrg) => ({
          ...crmOrg,
          name: crmOrg.orgType === OrgType.Hubspot && !crmOrg.name ? 'Hubspot' : crmOrg.name,
        }));
      }
      state.crmOrgsWasLoaded = true;
    },
    setOrgsAreReady: (state) => {
      state.crmOrgsAreReady = true;
    },
    setCrmOrg: (state, action: PayloadAction<{ crmOrg: CrmOrg }>) => {
      const idx = state.crmOrgs.findIndex((crmOrg) => crmOrg.id === action.payload.crmOrg.id);
      if (idx >= 0) {
        // Merge new data with existing org
        // Special handling for fetchStatus to avoid race conditions:
        // - If incoming has a final status (not Fetching), always use it (WebSocket update)
        // - If incoming is Fetching but existing has a final status, keep existing
        //   (handles race where connection form's addCrmOrg runs after WebSocket)
        const existingOrg = state.crmOrgs[idx];
        const incomingOrg = action.payload.crmOrg;

        const shouldPreserveExistingStatus =
          incomingOrg.fetchStatus === FetchStatus.Fetching &&
          existingOrg.fetchStatus &&
          existingOrg.fetchStatus !== FetchStatus.Fetching;

        state.crmOrgs[idx] = {
          ...existingOrg,
          ...incomingOrg,
          fetchStatus: shouldPreserveExistingStatus
            ? existingOrg.fetchStatus
            : incomingOrg.fetchStatus,
        };
      } else {
        // Add new org if not found (handles race condition where WebSocket
        // notification arrives before frontend adds the org to state)
        state.crmOrgs.push(action.payload.crmOrg);
      }
    },
    deleteCrmOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const idx = state.crmOrgs.findIndex((crmOrg) => crmOrg.id === action.payload.crmOrgId);

      if (idx >= 0) {
        state.crmOrgs.splice(idx, 1);
      }
    },
    addFetchingToOrgId: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const crmOrg = state.crmOrgs.find((crmOrg) => crmOrg.id === action.payload.crmOrgId);
      if (crmOrg) {
        crmOrg.fetchStatus = FetchStatus.Fetching;
      }
    },
    updateCrmOrgName: (state, action: PayloadAction<{ orgId: string; name: string }>) => {
      const org = state.crmOrgs.find((crmOrg) => crmOrg.id === action.payload.orgId);
      if (org) {
        org.name = action.payload.name;
      }
    },
    setIsLoadingObjectTypes: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
      }>,
    ) => {
      const { crmOrgId } = action.payload;
      const _map = state.crmOrgsMap[crmOrgId];
      state.crmOrgsMap[crmOrgId] = {
        ..._map,
        loadingObjectTypes: true,
      };
    },
    setAndSortObjectTypes: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        objectTypes: ObjectTypeName[];
      }>,
    ) => {
      const { crmOrgId, objectTypes } = action.payload;
      if (!crmOrgId) {
        return;
      }
      const objectTypesSorted = objectTypes
        ?.map((obj) => ({ ...obj, label: obj.label.toString() }))
        .sort(sortByLabel);

      state.crmOrgsMap[crmOrgId] = {
        loadingObjectTypes: false,
        objectTypes: objectTypesSorted,
      };
    },
    updatePermissionSetGroupAssigned: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      const idx = state.crmOrgs.findIndex((crmOrg) => crmOrg.id === crmOrgId);
      if (idx >= 0) {
        state.crmOrgs[idx].isPermissionSetGroupAssigned = true;
      }
    },
  },
});

export const {
  setCrmOrgs,
  setCrmOrg,
  addFetchingToOrgId,
  deleteCrmOrg,
  setAndSortObjectTypes,
  setIsLoadingObjectTypes,
  updatePermissionSetGroupAssigned,
  setOrgsAreReady,
  updateCrmOrgName,
} = environmentsSlice.actions;

export const getCrmOrgById = (crmOrgId?: string) => (state: RootState) =>
  state.environments.crmOrgs.find((crmOrg) => crmOrg.id === crmOrgId);

export const selectCrmOrgs = (state: RootState) => state.environments.crmOrgs;

export const selectConnectedCrmOrgs = createSelector([selectCrmOrgs], (crmOrgs) =>
  crmOrgs.filter(({ isConnected }) => isConnected),
);

export const selectFirstConnectedCrmOrg = (state: RootState) =>
  state.environments.crmOrgs?.filter(({ isConnected }) => isConnected)[0];

export const selectCrmOrg = (id: string | undefined) => (state: RootState) => {
  const idx = state.environments.crmOrgs.findIndex((crmOrg) => crmOrg.id === id);
  if (idx >= 0) {
    return state.environments.crmOrgs[idx];
  }
};

export const selectCrmOrgHasManagePackage = (crmOrgId: string) => (state: RootState) =>
  state.environments.crmOrgs.find((crmOrg) => crmOrg.id === crmOrgId)?.isManagedPackageInstalled;

export const selectCrmOrgObjectTypes = (crmOrgId: string) => (state: RootState) =>
  state.environments.crmOrgsMap[crmOrgId]?.objectTypes;

export const selectIsLoadingObjectTypes = (crmOrgId: string) => (state: RootState) =>
  state.environments.crmOrgsMap[crmOrgId]?.loadingObjectTypes;

export const selectCrmOrgsMap = (state: RootState) => state.environments.crmOrgsMap;

export const selectProductionCrmOrg = (state: RootState) =>
  state.environments.crmOrgs.find(
    (crmOrg) => crmOrg.isMain && crmOrg.isConnected && crmOrg.orgType === OrgType.Salesforce,
  );

export const selectDefaultCreationEnvironment = (state: RootState) => {
  const idx = state.environments.crmOrgs.findIndex(
    (crmOrg) => crmOrg.id === state.userInfo.data?.preferences?.defaultCreationCrmOrgId,
  );
  if (idx >= 0) {
    return state.environments.crmOrgs[idx];
  }
};

export const selectCrmOrgsWasLoaded = (state: RootState) => state.environments.crmOrgsWasLoaded;

export const selectCrmOrgsAreReady = (state: RootState) => state.environments.crmOrgsAreReady;

export const selectHasConnectedCrmOrgs = (state: RootState) =>
  state.environments.crmOrgs.some((crmOrg) => crmOrg.isConnected);

export default environmentsSlice.reducer;
